/**
 * ProfileComplete v2.0 — multi-step onboarding wizard.
 *
 * Route: /app/profile/complete
 *
 * Replaces the legacy 5121-line god-component. State + API orchestration
 * lives here; rendering for each step is delegated to a step component
 * under `frontend/src/components/profileComplete/` (and the existing
 * extracted `EstablishmentStep.js`).
 *
 * Skill compliance:
 *   - P5  inline validation (each step renders its own errors)
 *   - P6  page header (text-xl font-semibold)
 *   - P9  inputMode/autoCapitalize (in step components)
 *   - P13 500ms debounce on text searches (in step components)
 *   - P15 KPI / progress strip
 *   - P20 canonical page skeleton (grid > col-12 > Card)
 *   - P22 incremental loading per step
 *   - Hard rule 6: zero hex colors. severity Tags + var tokens via PrimeReact.
 *
 * Preserved verbatim from legacy:
 *   - All API endpoints (auth/user, user-details/*, location/*, establishment/*,
 *     credit-recharge/*)
 *   - Cross-tenant Razorpay redirect flow (in RechargeBlock)
 *   - profileCompletionService.forceRefresh() side-effects after each save
 *   - Permission checks via profile_completion hook
 */
import { Fragment, useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Divider } from 'primereact/divider';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';

import axiosInstance from '../config/axios.config';
import establishmentService from '../services/establishmentService';
import profileCompletionService from '../services/profileCompletionService';
import { useProfileCompletion } from '../hooks/useProfileCompletion';

import BasicInfoStep from '../components/profileComplete/BasicInfoStep';
import UserTypeStep from '../components/profileComplete/UserTypeStep';
import EstablishmentStep from '../components/EstablishmentStep';
import DocumentsStep from '../components/profileComplete/DocumentsStep';
import LivenessStep from '../components/profileComplete/LivenessStep';
import CreditsStep from '../components/profileComplete/CreditsStep';
import PreviewStep from '../components/profileComplete/PreviewStep';
import { validateFileType, validateFileSize, parseBackendErrors } from '../components/profileComplete/validators';

// Compact wizard-footer button styling — tighter padding than default
// p-button-sm so Back / Next don't feel oversized in the action bar.
const COMPACT_BTN_PT = {
    root: { style: { padding: '0.35rem 0.75rem', fontSize: '0.8rem' } },
};

/* Toast styling — clean, slim, production-grade.
   - Rounded corners, soft shadow, compact padding
   - Bigger icon, smaller summary, even smaller detail (info hierarchy)
   - Severity-tinted left border accent (Linear / GitHub pattern)
   - Restrained colors: pastel surface, stronger accent only on the icon + border */
const TOAST_PT = {
    message: {
        style: {
            borderRadius: '0.5rem',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
            padding: '0.5rem 0.75rem',
            margin: '0 0 0.5rem 0',
            borderWidth: '0 0 0 3px',
            borderStyle: 'solid',
        },
    },
    messageContent: {
        style: { padding: '0.5rem', alignItems: 'flex-start' },
    },
    messageIcon: {
        style: { fontSize: '1.1rem', marginRight: '0.5rem', marginTop: '0.1rem' },
    },
    summary: {
        style: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 },
    },
    detail: {
        style: { fontSize: '0.8rem', fontWeight: 400, lineHeight: 1.4, marginTop: '0.15rem' },
    },
    closeButton: {
        style: { width: '1.5rem', height: '1.5rem' },
    },
};

/* Master step list. The actual STEPS rendered for a user is derived from this
   inside the component — company employees skip the 'credits' step. */
const BASE_STEPS = [
    { key: 'basic',         label: 'Basic info',           shortLabel: 'Basic',    icon: 'pi-id-card' },
    { key: 'user_type',     label: 'Your role',            shortLabel: 'Role',     icon: 'pi-tags' },
    { key: 'establishment', label: 'Establishment',        shortLabel: 'Estab.',   icon: 'pi-building' },
    { key: 'documents',     label: 'Documents',            shortLabel: 'Docs',     icon: 'pi-file' },
    { key: 'liveness',      label: 'Verify yourself',      shortLabel: 'Selfie',   icon: 'pi-camera' },
    { key: 'credits',       label: 'Credits',              shortLabel: 'Credits',  icon: 'pi-wallet', optional: true },
    { key: 'review',        label: 'Review & submit',      shortLabel: 'Review',   icon: 'pi-check-square' },
];

const formatDocumentLabel = (fieldName) => {
    if (!fieldName) return '';
    return fieldName
        .replace(/_doc$/, '')
        .replace(/_/g, ' ')
        .split(' ')
        .map((w) => {
            if (w.toLowerCase() === 'pan') return 'PAN';
            if (w.toLowerCase() === 'gst') return 'GST';
            if (w.toLowerCase() === 'tan') return 'TAN';
            if (w.toLowerCase() === 'cin') return 'CIN';
            if (w.toLowerCase() === 'rbi') return 'RBI';
            if (w.toLowerCase() === 'aadhar' || w.toLowerCase() === 'aadhaar') return 'Aadhaar';
            return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
        })
        .join(' ');
};

/* ════════════════════════════════════════════════════════════════════ */
const ProfileComplete = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const { refreshProfile } = useProfileCompletion();

    /* ── current user (for RechargeBlock) ────────────────────────── */
    const userData = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); }
        catch { return {}; }
    }, []);
    const userId = userData?.id;

    /* ── wizard state ────────────────────────────────────────────── */
    /* Read ?step= from the URL so the read-only Profile page (and any
       other deep link) can land directly on a specific step. Clamp to
       valid range, fall back to 0. */
    const [searchParams] = useSearchParams();
    const initialStep = useMemo(() => {
        const raw = parseInt(searchParams.get('step'), 10);
        if (Number.isNaN(raw)) return 0;
        // Clamp against BASE_STEPS here because the dynamic STEPS isn't
        // initialised yet at this point in the component body. Out-of-range
        // values get caught by the key-based renderer downstream anyway.
        return Math.max(0, Math.min(raw, BASE_STEPS.length - 1));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [stepIndex, setStepIndex] = useState(initialStep);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [saving, setSaving] = useState(false);

    /* ── Step 1 ─ Basic info ─────────────────────────────────────── */
    const [basicInfo, setBasicInfo] = useState({
        first_name: '', last_name: '', mobile_number: '', email: '',
        company_name: '', address: '', city: '', state: '', pincode: '',
    });
    const [basicInfoErrors, setBasicInfoErrors] = useState({});
    const [pincodeMessage, setPincodeMessage] = useState({ show: false, type: '', text: '' });
    const [showLocationFields, setShowLocationFields] = useState(true);
    const [pincodeLoading, setPincodeLoading] = useState(false);

    /* ── Step 2 ─ User type ──────────────────────────────────────── */
    const [userType, setUserType] = useState({
        user_type_selections: [],
        company_name: '',
        total_loan_disbursements_per_month: '',
        support_needed_cities: [],
        referral_loan_products: [],
        loan_products_handled: [],
        active_state: '',
        active_cities: [],
    });
    const [userTypeErrors, setUserTypeErrors] = useState({});

    /* ── Step 3 ─ Establishment (uses extracted EstablishmentStep) ── */
    const [establishmentInfo, setEstablishmentInfo] = useState({
        type_of_establishment: '',
        bank_account_name: '',
        account_number: '',
        account_type: '',
        ifsc_code: '',
        bank_name: '',
        has_gst_certificate: false,
    });
    const [establishmentErrors, setEstablishmentErrors] = useState({});

    /* ── Step 4 ─ Documents ──────────────────────────────────────── */
    const [requiredDocuments, setRequiredDocuments] = useState([]);
    const [uploadedDocuments, setUploadedDocuments] = useState({});
    const [uploadingDocument, setUploadingDocument] = useState(null);
    const [replacingDocument, setReplacingDocument] = useState(null);

    /* ── Step 5 ─ Liveness verification ──────────────────────────── */
    const [liveness, setLiveness] = useState({ verified: false, verified_at: null });

    /* ── Employee inheritance ──────────────────────────────────────────────
     * When the user is a company employee, the establishment step should be
     * read-only (data inherited from the company owner) and the documents
     * step should ask for only Aadhar + PAN. The backend signals both via
     * `establishment_inherited` and `is_company_employee` on /user-details/profile.
     */
    const [establishmentInherited, setEstablishmentInherited] = useState(false);
    const [isCompanyEmployee, setIsCompanyEmployee] = useState(false);

    /* Wizard step list derived from BASE_STEPS — employees skip 'credits'. */
    const STEPS = useMemo(
        () => BASE_STEPS.filter((s) => !(isCompanyEmployee && s.key === 'credits')),
        [isCompanyEmployee],
    );

    /* ── Initial load: user + profile data ───────────────────────── */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // 1. Auth user → seed basic info
                const authRes = await axiosInstance.get('/auth/user');
                if (!cancelled && authRes.data?.success && authRes.data.user) {
                    const u = authRes.data.user;
                    const ud = u.user_detail || {};
                    setBasicInfo((prev) => ({
                        ...prev,
                        first_name: ud.first_name || prev.first_name,
                        last_name: ud.last_name || prev.last_name,
                        mobile_number: u.mobile_number || ud.mobile_number || prev.mobile_number,
                        email: u.email || prev.email,
                    }));
                }

                // 2. Existing profile data → pre-fill all steps
                const profRes = await axiosInstance.get('/user-details/profile');
                if (!cancelled && profRes.data?.success) {
                    const data = profRes.data.data || {};
                    if (data.profile_completion) refreshProfile();

                    // Employee mode flags — drive read-only establishment + reduced document set
                    setIsCompanyEmployee(!!data.is_company_employee);
                    setEstablishmentInherited(!!data.establishment_inherited);

                    // Liveness — read step5 from backend so re-entry skips capture
                    if (data.profile_completion?.step5_completed) {
                        setLiveness({
                            verified:    true,
                            verified_at: data.profile_completion.step5_completed_at || null,
                        });
                    }

                    if (data.basic_info) {
                        setBasicInfo((prev) => ({
                            ...prev,
                            first_name: data.basic_info.first_name || prev.first_name,
                            last_name: data.basic_info.last_name || prev.last_name,
                            mobile_number: data.basic_info.mobile_number || prev.mobile_number,
                            email: data.email || prev.email,
                            company_name: data.basic_info.company_name || prev.company_name,
                            address: data.basic_info.address || prev.address,
                            city: data.basic_info.city || prev.city,
                            state: data.basic_info.state || prev.state,
                            pincode: data.basic_info.pincode || prev.pincode,
                        }));
                    }

                    if (data.user_type) {
                        setUserType((prev) => ({
                            ...prev,
                            user_type_selections: data.user_type.user_type_selections || [],
                            company_name: data.user_type.company_name || '',
                            total_loan_disbursements_per_month: data.user_type.total_loan_disbursements_per_month || '',
                        }));
                    }

                    if (data.loan_agent_details) {
                        const ad = data.loan_agent_details;
                        const split = (v) => Array.isArray(v) ? v
                            : typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter(Boolean)
                            : [];
                        setUserType((prev) => ({
                            ...prev,
                            referral_loan_products: split(ad.referral_loan_products),
                            loan_products_handled: split(ad.loan_products_handled),
                            support_needed_cities: split(ad.support_needed_cities),
                            active_cities: split(ad.active_cities),
                            active_state: ad.active_state || '',
                        }));
                    }

                    /* ── Step 3: Establishment ─ spread the full
                       `getRelevantFields()` payload so dynamic fields
                       (designation, gst_certificate_number, individual_name,
                       all <type>_name / <type>_partner / <type>_pan / etc.)
                       actually re-load on edit. Strip meta flags. */
                    if (data.establishment_info) {
                        const e = data.establishment_info;
                        // Bank-name collision guard: when establishment type is
                        // 'bank', `establishment_info.bank_name` is the bank's
                        // own name; `bank_details.bank_name` (mapped below) is
                        // the user's banking partner. We keep
                        // `establishmentInfo.bank_name` reserved for banking
                        // partner — when type is 'bank' the establishment-side
                        // `bank_name` is already represented by other fields
                        // (bank_authorized_person_name etc.), so we drop it.
                        const {
                            documents_uploaded: _du,
                            is_complete: _ic,
                            bank_name: estBankName,
                            has_gst_certificate: _gst,
                            type_of_establishment: _type,
                            ...rawEstablishmentFields
                        } = e;
                        // Coerce nulls → '' so React inputs stay controlled
                        // (no "changing uncontrolled to controlled" warning).
                        const establishmentFields = Object.fromEntries(
                            Object.entries(rawEstablishmentFields).map(([k, v]) => [k, v ?? ''])
                        );
                        setEstablishmentInfo((prev) => ({
                            ...prev,
                            ...establishmentFields,
                            type_of_establishment: e.type_of_establishment || prev.type_of_establishment,
                            has_gst_certificate: !!e.has_gst_certificate,
                            // Reinstate establishment bank_name only when
                            // type isn't 'bank' (no collision in that case).
                            ...(estBankName && e.type_of_establishment !== 'bank'
                                ? { bank_name: estBankName }
                                : {}),
                        }));
                    }

                    /* ── Step 3 cont. — banking partner details (overlays only
                       the bank_* keys; legitimate winner over establishment
                       `bank_name` when type is 'bank' because that's the
                       intended field for the user's banking partner). */
                    if (data.bank_details) {
                        const b = data.bank_details;
                        setEstablishmentInfo((prev) => ({
                            ...prev,
                            bank_account_name: b.bank_account_name || prev.bank_account_name,
                            account_number: b.account_number || prev.account_number,
                            account_type: b.account_type || prev.account_type,
                            ifsc_code: b.ifsc_code || prev.ifsc_code,
                            bank_name: b.bank_name || prev.bank_name,
                        }));
                    }

                    /* ── Step 4: Documents ─ backend returns an ARRAY of doc
                       records, each keyed by `document_type` (the field name
                       used elsewhere in the wizard, e.g. "nbfc_pan_card").
                       The previous code used Object.entries() which yielded
                       numeric indices, so DocumentsStep's lookup
                       `uploadedDocuments[doc.name]` always returned undefined
                       and uploaded files appeared empty on edit. */
                    if (Array.isArray(data.documents)) {
                        const next = {};
                        data.documents.forEach((doc) => {
                            if (!doc) return;
                            const key = doc.document_type
                                || doc.document_field_name
                                || doc.field_name;
                            if (!key) return;
                            next[key] = {
                                id: doc.id || doc.document_id,
                                file_name: doc.file_name || doc.original_file_name || doc.original_name,
                                status: doc.status || doc.upload_status,
                                document_url: doc.document_url || doc.file_url || doc.s3_url || doc.download_url || doc.url,
                            };
                        });
                        setUploadedDocuments(next);
                    }
                }
            } catch {
                // silent — user can fill from scratch
            } finally {
                if (!cancelled) setLoadingProfile(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Reload required-document list whenever establishment type changes ─ */
    useEffect(() => {
        if (!establishmentInfo.type_of_establishment) {
            setRequiredDocuments([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const response = await establishmentService.getEstablishmentRequirements(
                    establishmentInfo.type_of_establishment
                );
                if (cancelled) return;
                if (response.data?.success) {
                    const list = (response.data.data?.required_documents || []).map((doc) => ({
                        name: doc.field_name,
                        label: formatDocumentLabel(doc.field_name),
                        is_mandatory: doc.is_required,
                        allowed_formats: doc.allowed_formats || ['pdf', 'jpg', 'jpeg', 'png'],
                        max_file_size: doc.max_file_size || 5_000_000,
                        category: doc.document_category || 'Documents',
                    }));

                    // Conditionally toggle GST certificate requirement
                    const idx = list.findIndex((d) => d.name === 'gst_certificate_doc');
                    if (establishmentInfo.has_gst_certificate) {
                        if (idx === -1) {
                            list.push({
                                name: 'gst_certificate_doc',
                                label: 'GST Certificate',
                                is_mandatory: true,
                                allowed_formats: ['pdf', 'jpg', 'jpeg', 'png'],
                                max_file_size: 5_000_000,
                                category: 'Business',
                            });
                        } else {
                            list[idx].is_mandatory = true;
                        }
                    } else if (idx !== -1) {
                        list[idx].is_mandatory = false;
                    }

                    setRequiredDocuments(list);
                }
            } catch {
                // silent — user sees an empty doc list with the gating Message
            }
        })();
        return () => { cancelled = true; };
    }, [establishmentInfo.type_of_establishment, establishmentInfo.has_gst_certificate]);

    /* ── Toast helpers — calmer copy, severity-aware life durations ──
       - Errors stay 5s so the user has time to read and react
       - Success messages dismiss in 2s — they're confirmations, not blockers
       - Warnings (validation guidance) stay 4s
       - All toasts are dismissible so the user can clear them manually */
    const showError = (summary, detail) =>
        toast.current?.show({ severity: 'error', summary, detail, life: 5000, closable: true });
    const showWarn = (summary, detail) =>
        toast.current?.show({ severity: 'warn', summary, detail, life: 4000, closable: true });
    const showSuccess = (summary, detail) =>
        toast.current?.show({ severity: 'success', summary, detail, life: 2000, closable: true });

    /* Backend error mapping — Laravel 422 → per-field UI errors.
       Routes field errors into the matching step's error state and surfaces
       general/business messages via a toast. */
    const handleBackendError = (error, setStepErrors, fallbackDetail) => {
        const parsed = parseBackendErrors(error);
        const hasFieldErrors = Object.keys(parsed.fields).length > 0;
        if (hasFieldErrors && setStepErrors) {
            setStepErrors((prev) => ({ ...prev, ...parsed.fields }));
        }
        if (parsed.general.length > 0) {
            showError("Couldn't save", parsed.general[0]);
        } else if (hasFieldErrors) {
            const count = Object.keys(parsed.fields).length;
            showWarn(
                'Check the highlighted fields',
                `${count} field${count > 1 ? 's' : ''} need attention before we can save.`
            );
        } else {
            showError("Couldn't save", fallbackDetail);
        }
        setTimeout(focusFirstError, 100);
    };

    /* Focus the first field with an inline error (scrolls into view and
       moves keyboard focus). Required for accessibility per P5 / WCAG. */
    const focusFirstError = () => {
        const candidates = document.querySelectorAll(
            '[aria-invalid="true"], .p-invalid input, .p-invalid textarea, .p-invalid'
        );
        for (const el of candidates) {
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if (typeof el.focus === 'function') {
                    try { el.focus({ preventScroll: true }); } catch { /* noop */ }
                }
                break;
            }
        }
    };

    const saveBasicInfo = async () => {
        const errs = BasicInfoStep.runValidations(basicInfo, { showLocationFields });
        setBasicInfoErrors(errs);
        if (BasicInfoStep.hasErrors(errs)) {
            showWarn('Check the highlighted fields', 'A few details need a quick fix before we can save.');
            setTimeout(focusFirstError, 100);
            return false;
        }
        setSaving(true);
        try {
            const res = await axiosInstance.post('/user-details/basic-info', basicInfo);
            if (!res.data?.success) throw new Error(res.data?.message || 'Save failed');
            await profileCompletionService.forceRefresh();
            showSuccess('All saved', 'Your details are safely stored.');
            return true;
        } catch (e) {
            handleBackendError(e, setBasicInfoErrors, "Couldn't save your basic information. Please try again.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const saveUserType = async () => {
        const errs = UserTypeStep.runValidations(userType, { isCompanyEmployee });
        setUserTypeErrors(errs);
        if (UserTypeStep.hasErrors(errs)) {
            const firstErr = Object.values(errs).find((e) => e && e !== '') || 'A few details need attention.';
            showWarn('Check the highlighted fields', firstErr);
            setTimeout(focusFirstError, 100);
            return false;
        }
        setSaving(true);
        try {
            const res = await axiosInstance.post('/user-details/user-type', userType);
            if (!res.data?.success) throw new Error(res.data?.message || 'Save failed');
            await profileCompletionService.forceRefresh();
            showSuccess('All saved', 'Your role and focus are saved.');
            return true;
        } catch (e) {
            handleBackendError(e, setUserTypeErrors, "Couldn't save your role information. Please try again.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const saveEstablishment = async () => {
        // Employees inherit establishment from the company owner — there's
        // nothing to save here. Just refresh status and advance.
        if (isCompanyEmployee) {
            try {
                await profileCompletionService.forceRefresh();
            } catch (_e) { /* non-fatal */ }
            return true;
        }

        const errs = EstablishmentStep.runValidations(establishmentInfo);
        setEstablishmentErrors(errs);
        if (EstablishmentStep.hasErrors(errs)) {
            const firstErr = Object.values(errs).find((e) => e && e !== '') || 'A few details need attention.';
            showWarn('Check the highlighted fields', firstErr);
            setTimeout(focusFirstError, 100);
            return false;
        }
        setSaving(true);
        try {
            const res = await establishmentService.saveEstablishmentDetails(establishmentInfo);
            if (!res.data?.success) throw new Error(res.data?.message || 'Save failed');
            await profileCompletionService.forceRefresh();
            showSuccess('All saved', 'Establishment details saved.');
            return true;
        } catch (e) {
            handleBackendError(e, setEstablishmentErrors, "Couldn't save establishment details. Please try again.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    /* ── Step 4 doc handlers ────────────────────────────────────── */
    const onDocUpload = useCallback(async (file, doc, oldDocId) => {
        // Belt-and-braces: re-validate on the way in (DocumentsStep already
        // pre-validates, but a third party wrapping this could call directly).
        const typeErr = validateFileType(file, doc.allowed_formats);
        const sizeErr = !typeErr ? validateFileSize(file, doc.max_file_size) : '';
        const err = typeErr || sizeErr;
        if (err) {
            showWarn(doc.label || doc.name, err);
            return;
        }

        setUploadingDocument(doc.name);
        const formData = new FormData();
        formData.append('document', file);
        formData.append('document_field_name', doc.name);

        try {
            if (oldDocId) {
                try { await establishmentService.deleteDocument(oldDocId); } catch { /* silent */ }
            }
            const res = await axiosInstance.post('/user-details/establishment/document/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (res.data?.success) {
                setUploadedDocuments((prev) => ({
                    ...prev,
                    [doc.name]: {
                        id: res.data.data?.document_id,
                        file_name: res.data.data?.file_name,
                        status: res.data.data?.status,
                        document_url: res.data.data?.document_url,
                    },
                }));
                showSuccess(
                    oldDocId ? 'File replaced' : 'File uploaded',
                    doc.label || doc.name
                );
            } else {
                throw new Error(res.data?.message || 'Upload failed');
            }
        } catch (e) {
            showError(
                "Couldn't upload",
                e.response?.data?.message || e.message || 'Please try uploading the file again.'
            );
        } finally {
            setUploadingDocument(null);
            setReplacingDocument(null);
        }
    }, []);

    const onDocReplaceClick = useCallback((docName) => setReplacingDocument(docName), []);
    const onDocCancelReplace = useCallback(() => setReplacingDocument(null), []);

    const onDocDelete = useCallback(async (docName, docId) => {
        if (!docId) return;
        setSaving(true);
        try {
            await establishmentService.deleteDocument(docId);
            setUploadedDocuments((prev) => {
                const next = { ...prev };
                delete next[docName];
                return next;
            });
            showSuccess('File removed', '');
        } catch (e) {
            showError(
                "Couldn't remove",
                e.response?.data?.message || 'Please try again in a moment.'
            );
        } finally {
            setSaving(false);
        }
    }, []);

    const onDocView = useCallback((url) => {
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }, []);

    /* ── Final submit (step 5) ───────────────────────────────────── */
    /* Re-validates EVERY step before submitting. If any fails, set that
       step's error state, jump back to the first failing step, focus the
       first invalid field. Stripe / Razorpay onboarding follow this same
       last-mile guard pattern. */
    const submitProfile = async () => {
        const basicErrs = BasicInfoStep.runValidations(basicInfo, { showLocationFields });
        const userErrs = UserTypeStep.runValidations(userType, { isCompanyEmployee });
        // Employees inherit establishment from the company owner — skip validation.
        const estErrs = isCompanyEmployee ? {} : EstablishmentStep.runValidations(establishmentInfo);
        const docErrs = DocumentsStep.runValidations(uploadedDocuments, requiredDocuments);
        const liveErrs = LivenessStep.runValidations(liveness);

        const failingStep = BasicInfoStep.hasErrors(basicErrs) ? 0
                         : UserTypeStep.hasErrors(userErrs) ? 1
                         : EstablishmentStep.hasErrors(estErrs) ? 2
                         : DocumentsStep.hasErrors(docErrs) ? 3
                         : LivenessStep.hasErrors(liveErrs) ? 4
                         : -1;

        if (failingStep >= 0) {
            // Push the relevant errors into the matching step's error state
            setBasicInfoErrors(basicErrs);
            setUserTypeErrors(userErrs);
            setEstablishmentErrors(estErrs);

            // Jump to first failing step
            setStepIndex(failingStep);
            window.scrollTo({ top: 0, behavior: 'smooth' });

            // Friendly, action-oriented message
            const stepNames = ['Basic info', 'Your role', 'Establishment', 'Documents', 'Verify yourself'];
            if (failingStep === 3) {
                showWarn('Almost there!', docErrs.documents);
            } else if (failingStep === 4) {
                showWarn('One more step', liveErrs.verified || 'Please complete the live-selfie verification.');
            } else {
                const errsMap = [basicErrs, userErrs, estErrs][failingStep];
                const firstErr = Object.values(errsMap).find((e) => e && e !== '') || 'A few details need attention.';
                showWarn(`Quick fix needed in ${stepNames[failingStep]}`, firstErr);
            }
            // Wait for the step swap to render then focus the first error
            setTimeout(focusFirstError, 250);
            return;
        }

        setSaving(true);
        try {
            const res = await axiosInstance.post('/user-details/documents/complete', {
                submitted_at: new Date().toISOString(),
            });
            if (res.data?.success) {
                await profileCompletionService.forceRefresh();
                toast.current?.show({
                    severity: 'success',
                    summary: 'Profile complete!',
                    detail: 'You now have full access to the platform. Redirecting to dashboard…',
                    life: 4000,
                });
                setTimeout(() => navigate('/app/dashboard'), 2000);
            } else {
                throw new Error(res.data?.message || 'Failed to submit profile');
            }
        } catch (e) {
            const parsed = parseBackendErrors(e);
            const detail = parsed.general[0]
                || e.response?.data?.message
                || e.message
                || 'Could not submit your profile.';
            showError("Couldn't submit your profile", detail);
        } finally {
            setSaving(false);
        }
    };

    /* ── Step navigation ─────────────────────────────────────────── */
    const handleNext = async () => {
        let ok = true;
        if (stepIndex === 0) ok = await saveBasicInfo();
        else if (stepIndex === 1) ok = await saveUserType();
        else if (stepIndex === 2) ok = await saveEstablishment();
        else if (stepIndex === 3) ok = true; // Documents save on upload
        else if (stepIndex === 4) {
            // Liveness step — block forward nav until verified
            if (!liveness.verified) {
                showWarn('One more step', 'Please complete the live-selfie verification before continuing.');
                ok = false;
            }
        }
        else if (STEPS[stepIndex]?.key === 'credits') {
            // Credits step is optional — always allow advancing.
            ok = true;
        }
        if (ok && stepIndex < STEPS.length - 1) {
            setStepIndex((s) => s + 1);
            // Scroll to top so the user sees the next step's heading
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        if (stepIndex > 0) {
            setStepIndex((s) => s - 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    /* ── Loading state ───────────────────────────────────────────── */
    if (loadingProfile) {
        return (
            <div className="grid">
                <div className="col-12">
                    <div className="mb-4">
                        <Skeleton width="14rem" height="1.75rem" className="mb-2" />
                        <Skeleton width="22rem" height="1rem" />
                    </div>
                    <Card className="shadow-1">
                        <Skeleton width="100%" height="3.5rem" className="mb-4" />
                        <Skeleton width="40%" height="1.25rem" className="mb-3" />
                        <Skeleton width="100%" height="2.5rem" className="mb-2" />
                        <Skeleton width="100%" height="2.5rem" className="mb-2" />
                        <Skeleton width="100%" height="2.5rem" />
                    </Card>
                </div>
            </div>
        );
    }

    /* ── Render ──────────────────────────────────────────────────── */
    const isLast = stepIndex === STEPS.length - 1;
    const currentStep = STEPS[stepIndex];

    /* Derive step completedness from current form state.
       A step is "completed" when its data validates with no errors.
       This auto-detects pre-filled steps on initial load too. */
    const isStepCompleted = (i) => {
        if (i === 0) {
            return !BasicInfoStep.hasErrors(
                BasicInfoStep.runValidations(basicInfo, { showLocationFields })
            );
        }
        if (i === 1) {
            return !UserTypeStep.hasErrors(UserTypeStep.runValidations(userType, { isCompanyEmployee }));
        }
        if (i === 2) {
            return !!establishmentInfo.type_of_establishment;
        }
        if (i === 3) {
            // Documents step is "complete" only when at least the mandatory ones are uploaded.
            // If there are no required docs (e.g. establishment type doesn't need any), treat as complete.
            return !DocumentsStep.hasErrors(
                DocumentsStep.runValidations(uploadedDocuments, requiredDocuments)
            );
        }
        if (i === 4) {
            // Liveness step is complete iff the user has passed verification.
            return !LivenessStep.hasErrors(LivenessStep.runValidations(liveness));
        }
        if (i === 5) {
            // Credits step is OPTIONAL — always treated as completed so it
            // never blocks navigation to the Review step. Whether the user
            // bought credits is irrelevant to profile completion.
            return true;
        }
        return false; // Review step has no own data to validate
    };

    /* Reachability — distinct from "completed".
       The review step has no own data but IS reachable when every prior step
       is completed. Keeping this separate from isStepCompleted avoids
       semantic confusion (review is never "done", it's just a destination). */
    const isStepReachable = (i) => {
        if (i === STEPS.length - 1) {
            // Review step: reachable iff every input step is completed
            // (credits at index 5 is optional and always counts as completed).
            return [0, 1, 2, 3, 4, 5].every((k) => isStepCompleted(k));
        }
        return isStepCompleted(i);
    };

    /* User can jump to step i if:
       - it's the current step, OR
       - it's any earlier step (always allowed to go back), OR
       - it's reachable (own data filled, OR for review: all priors completed) */
    const canNavigateTo = (i) => i === stepIndex || i < stepIndex || isStepReachable(i);

    return (
        <div className="grid">
            <div className="col-12">
                {/* Toast — slim production styling (Stripe / Linear-ish):
                    rounded, soft shadow, compact padding, severity-tinted
                    border-left accent. Top-right on desktop, top-center
                    breakout on mobile so it doesn't get clipped by chrome. */}
                <Toast
                    ref={toast}
                    position="top-right"
                    breakpoints={{ '768px': { width: '95vw', left: '2.5vw', right: '2.5vw' } }}
                    pt={TOAST_PT}
                />
                <ConfirmDialog />

                {/* ── 1. Page header (P6) — slim, no completion meter (Steps strip already shows progress) ─ */}
                <div className="mb-4">
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">Complete your profile</h1>
                    <p className="text-600 text-sm m-0">
                        A few quick steps to unlock the full platform.
                    </p>
                </div>

                {/* ── 2. Single Card — Steps + body + actions all together ── */}
                {/* Mirrors the admin pattern (LendingNetworkAdmin):
                    one Card containing the tabs/filter, the table/body,
                    and the paginator/actions. */}
                <Card pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}>
                    {/* Compact step strip — green check for done, primary for current.
                        Any previously-completed step is clickable so the user can
                        jump around without losing data. */}
                    <StepStrip
                        steps={STEPS}
                        activeIndex={stepIndex}
                        canNavigateTo={canNavigateTo}
                        onStepClick={(i) => {
                            if (canNavigateTo(i) && i !== stepIndex) {
                                setStepIndex(i);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                    />

                    {/* Step body (no inner Card — same surface as the steps strip above) */}
                    {stepIndex === 0 && (
                        <BasicInfoStep
                            value={basicInfo}
                            errors={basicInfoErrors}
                            onChange={(patch) => setBasicInfo((p) => ({ ...p, ...patch }))}
                            onErrors={(patch) => setBasicInfoErrors((p) => ({ ...p, ...patch }))}
                            pincodeMessage={pincodeMessage}
                            onPincodeMessage={setPincodeMessage}
                            showLocationFields={showLocationFields}
                            onShowLocationFields={setShowLocationFields}
                            pincodeLoading={pincodeLoading}
                            onPincodeLoading={setPincodeLoading}
                            saving={saving}
                        />
                    )}
                    {stepIndex === 1 && (
                        <UserTypeStep
                            value={userType}
                            errors={userTypeErrors}
                            onChange={(patch) => setUserType((p) => ({ ...p, ...patch }))}
                            onErrors={(patch) => setUserTypeErrors((p) => ({ ...p, ...patch }))}
                            saving={saving}
                            isCompanyEmployee={isCompanyEmployee}
                        />
                    )}
                    {stepIndex === 2 && (
                        <EstablishmentStep
                            establishmentInfo={establishmentInfo}
                            setEstablishmentInfo={setEstablishmentInfo}
                            toast={toast}
                            validationErrors={establishmentErrors}
                            basicInfo={basicInfo}
                            inherited={establishmentInherited}
                            isCompanyEmployee={isCompanyEmployee}
                        />
                    )}
                    {stepIndex === 3 && (
                        <DocumentsStep
                            requiredDocuments={requiredDocuments}
                            uploadedDocuments={uploadedDocuments}
                            uploadingDocument={uploadingDocument}
                            replacingDocument={replacingDocument}
                            establishmentTypeChosen={!!establishmentInfo.type_of_establishment}
                            onUpload={onDocUpload}
                            onReplaceClick={onDocReplaceClick}
                            onCancelReplace={onDocCancelReplace}
                            onDelete={onDocDelete}
                            onView={onDocView}
                            saving={saving}
                        />
                    )}
                    {stepIndex === 4 && (
                        <LivenessStep
                            value={liveness}
                            onChange={(patch) => setLiveness((p) => ({ ...p, ...patch }))}
                            saving={saving}
                            onError={(msg) => showError("Couldn't verify", msg)}
                        />
                    )}
                    {STEPS[stepIndex]?.key === 'credits' && (
                        <CreditsStep userId={userId} />
                    )}
                    {STEPS[stepIndex]?.key === 'review' && (
                        <PreviewStep
                            basicInfo={basicInfo}
                            userType={userType}
                            establishment={establishmentInfo}
                            uploadedDocuments={uploadedDocuments}
                            requiredDocuments={requiredDocuments}
                            onEditStep={(i) => {
                                setStepIndex(i);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                        />
                    )}

                    {/* Divider + action bar — last child of the Card.
                        Buttons use compact padding via pt to shrink them ~25%
                        smaller than default size="small". Back is text-only
                        (subtle), Next is filled (the only filled CTA on the
                        page). Labels shortened for tighter horizontal width. */}
                    <Divider className="my-3" />
                    <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
                        <Button
                            label="Back"
                            icon="pi pi-arrow-left"
                            text
                            severity="secondary"
                            size="small"
                            disabled={stepIndex === 0 || saving}
                            onClick={handleBack}
                            pt={COMPACT_BTN_PT}
                        />
                        <div className="text-500 text-xs hidden md:block">
                            Step {stepIndex + 1} of {STEPS.length} · {currentStep.label}
                        </div>
                        {isLast ? (
                            <Button
                                label="Submit"
                                icon="pi pi-check"
                                severity="success"
                                size="small"
                                loading={saving}
                                onClick={submitProfile}
                                pt={COMPACT_BTN_PT}
                            />
                        ) : (
                            <Button
                                /* On the optional Credits step, frame the
                                   primary action as "Skip" so the user
                                   never feels forced to buy. The recharge
                                   widget itself has its own "Recharge ₹X"
                                   button for users who do want to buy. */
                                label={STEPS[stepIndex]?.key === 'credits' ? 'Skip for now' : 'Next'}
                                icon={STEPS[stepIndex]?.key === 'credits' ? 'pi pi-forward' : 'pi pi-arrow-right'}
                                iconPos="right"
                                size="small"
                                severity={STEPS[stepIndex]?.key === 'credits' ? 'secondary' : undefined}
                                outlined={STEPS[stepIndex]?.key === 'credits'}
                                loading={saving}
                                onClick={handleNext}
                                pt={COMPACT_BTN_PT}
                            />
                        )}
                    </div>
                </Card>

            </div>
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════
 * StepStrip — compact horizontal step indicator.
 *
 *   ●━━━━●━━━━●━━━━○━━━━○
 *   ✓    ✓    ✓    4    5
 *   Basic Role Estab Docs Review
 *
 * - Done steps: green-500 fill + check icon, line to next is green-500
 * - Current step: primary-color fill + number
 * - Future steps: surface-100 fill + muted number, line is surface-200
 * - Done steps clickable (jump back); future steps disabled
 * - Same look on mobile + desktop; horizontally scrollable on narrow screens
 *   if labels overflow.
 * ════════════════════════════════════════════════════════════════════ */
const StepStrip = ({ steps, activeIndex, canNavigateTo, onStepClick }) => (
    <div className="flex align-items-start justify-content-between gap-1 mb-3 overflow-x-auto pb-1">
        {steps.map((step, i) => {
            const isDone = i < activeIndex;
            const isActive = i === activeIndex;
            const isFuture = i > activeIndex;
            // Clickable if the parent says so (back to any earlier step OR forward
            // to any previously-completed step) and it's not the current step.
            const clickable = canNavigateTo
                ? (canNavigateTo(i) && i !== activeIndex)
                : isDone;
            const isLast = i === steps.length - 1;

            return (
                <Fragment key={step.key}>
                    {/* Step button */}
                    <button
                        type="button"
                        onClick={() => clickable && onStepClick(i)}
                        disabled={!clickable && !isActive}
                        className="flex flex-column align-items-center gap-1 border-none bg-transparent p-0 flex-shrink-0"
                        style={{
                            cursor: clickable ? 'pointer' : 'default',
                            minWidth: '4rem',
                        }}
                        aria-current={isActive ? 'step' : undefined}
                        aria-label={`${step.label}${isDone ? ' (completed)' : isActive ? ' (current)' : ''}`}
                    >
                        <div
                            className="flex align-items-center justify-content-center border-round-2xl font-semibold transition-all transition-duration-150"
                            style={{
                                width: '1.75rem',
                                height: '1.75rem',
                                fontSize: '0.75rem',
                                backgroundColor: isDone
                                    ? 'var(--green-500)'
                                    : isActive
                                        ? 'var(--primary-color)'
                                        : 'var(--surface-100)',
                                color: isDone || isActive
                                    ? 'var(--primary-color-text)'
                                    : 'var(--text-color-secondary)',
                                border: isFuture ? '1px solid var(--surface-border)' : 'none',
                            }}
                        >
                            {isDone
                                ? <i className="pi pi-check" style={{ fontSize: '0.7rem' }} aria-hidden="true" />
                                : i + 1}
                        </div>
                        <span
                            className={classNames(
                                'text-xs',
                                isActive ? 'text-900 font-medium' : isDone ? 'text-700' : 'text-500'
                            )}
                            style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                        >
                            {step.shortLabel || step.label}
                        </span>
                    </button>

                    {/* Connector line — sits between buttons, aligned with the circles */}
                    {!isLast && (
                        <div
                            className="flex-1"
                            style={{
                                height: '2px',
                                marginTop: '0.875rem', // half of circle height (1.75rem / 2)
                                minWidth: '0.75rem',
                                backgroundColor: isDone ? 'var(--green-500)' : 'var(--surface-200)',
                                transition: 'background-color 0.15s',
                            }}
                            aria-hidden="true"
                        />
                    )}
                </Fragment>
            );
        })}
    </div>
);

export default ProfileComplete;
